import { useEffect, useRef, useState } from 'react';
import { api, ApiRequestError, type LobbyView } from '../api.ts';
import { useLobby } from '../useLobby.ts';
import { useSession } from '../session.tsx';
import { Avatar, Banner, Button, Card, Spinner } from '../components/ui.tsx';
import { EntrySubmit } from '../components/EntrySubmit.tsx';
import { LobbySettingsPanel } from '../components/LobbySettings.tsx';

/**
 * The host's control over one member's song count.
 *
 * A stepper rather than a text box: the useful moves are "one more" and "one
 * fewer" off whatever the planner already decided, and a free-text field in a
 * row this narrow invites a typo that breaks the bracket maths.
 *
 * `auto` is always on screen and reads as lit while the member is on the
 * planner's share, which is where everybody starts. Nudging the number turns
 * it off; pressing it hands the member back. Showing it only once a count had
 * been set made the untouched state look like a manual one.
 */
function SlotStepper({
  value,
  max,
  isCustom,
  onSet,
}: {
  value: number;
  max: number;
  isCustom: boolean;
  onSet: (slots: number | null) => void;
}) {
  const step =
    'rounded px-2 py-0.5 text-xs text-text-muted transition-colors hover:text-text ' +
    'disabled:cursor-not-allowed disabled:opacity-40';

  return (
    <div className="flex items-center gap-0.5 rounded-lg border border-border p-1">
      <button
        type="button"
        aria-label="One song fewer"
        disabled={value <= 0}
        onClick={() => onSet(value - 1)}
        className={step}
      >
        −
      </button>
      <span className="min-w-5 text-center text-xs tabular-nums">{value}</span>
      <button
        type="button"
        aria-label="One song more"
        disabled={value >= max}
        onClick={() => onSet(value + 1)}
        className={step}
      >
        +
      </button>
      <button
        type="button"
        aria-pressed={!isCustom}
        disabled={!isCustom}
        title={
          isCustom
            ? 'Back to an even share of the bracket'
            : 'On an even share of the bracket — use − or + to set a count by hand'
        }
        onClick={() => onSet(null)}
        className={
          'rounded px-2 py-0.5 text-xs transition-colors disabled:cursor-default ' +
          (isCustom ? 'text-text-muted hover:text-text' : 'bg-accent/15 text-accent')
        }
      >
        auto
      </button>
    </div>
  );
}

function MemberRow({
  member,
  isHost,
  canManage,
  canSetSlots,
  maxSlots,
  onToggleRole,
  onSetSlots,
}: {
  member: LobbyView['members'][number];
  isHost: boolean;
  canManage: boolean;
  /** The viewer is the host and this member is somebody who submits. */
  canSetSlots: boolean;
  maxSlots: number;
  onToggleRole: () => void;
  onSetSlots: (slots: number | null) => void;
}) {
  const done = member.role !== 'spectator' && member.submittedCount >= member.slotsAllotted;

  return (
    <li className="flex flex-wrap items-center gap-3 py-2">
      <span className="relative shrink-0">
        <Avatar value={member.avatarValue} size="sm" />
        <span
          aria-hidden
          title={member.isConnected ? 'online' : 'offline'}
          className={`absolute -right-0.5 -bottom-0.5 size-2.5 rounded-full border-2 border-surface ${
            member.isConnected ? 'bg-success' : 'bg-text-faint'
          }`}
        />
      </span>

      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-2 text-sm">
          <span className="truncate font-medium">{member.displayName}</span>
          {isHost && (
            <span className="rounded bg-accent/15 px-1.5 py-0.5 text-xs text-accent">host</span>
          )}
          {member.role === 'spectator' && (
            <span className="text-xs text-text-faint">watching</span>
          )}
        </p>
        {member.role !== 'spectator' && (
          // Counts only — never which entries. The whole game depends on that
          // staying hidden until the reveal.
          <p className={`text-xs ${done ? 'text-success' : 'text-text-faint'}`}>
            {member.submittedCount} / {member.slotsAllotted} submitted
            {member.slotsOverride !== null && (
              <span className="text-text-faint"> · set by the host</span>
            )}
          </p>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {canSetSlots && (
          // The host's own number when there is one, rather than the
          // allotment: a plan that cannot be satisfied zeroes every allotment,
          // and a stepper reading 0 would give the host no way back down from
          // the count that broke it.
          <SlotStepper
            value={member.slotsOverride ?? member.slotsAllotted}
            max={maxSlots}
            isCustom={member.slotsOverride !== null}
            onSet={onSetSlots}
          />
        )}
        {canManage && (
          <Button variant="ghost" onClick={onToggleRole}>
            {member.role === 'spectator' ? 'Make player' : 'Make watcher'}
          </Button>
        )}
      </div>
    </li>
  );
}

export function Lobby({
  lobbyId,
  onLeave,
  onOpenGame,
  suppressAutoOpenGame = false,
}: {
  lobbyId: string;
  onLeave: () => void;
  onOpenGame: () => void;
  suppressAutoOpenGame?: boolean;
}) {
  const { session } = useSession();
  const { lobby, error, connected, refresh } = useLobby(lobbyId);
  const [actionError, setActionError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  /**
   * Everybody follows the host into the game.
   *
   * There used to be a "Go to the game" button here, which meant the host
   * started a match and then waited while people noticed a prompt and clicked
   * it. A song battle is one room doing one thing at a time; being asked
   * whether you would like to join the thing you are already at is friction for
   * nothing.
   *
   * Only for a game in progress, and only once — a finished game still offers
   * the results rather than dragging somebody back into a bracket they have
   * already seen, and somebody who deliberately walked back to the lobby
   * mid-game is not hauled forward again.
   */
  const followedRef = useRef(false);
  useEffect(() => {
    if (suppressAutoOpenGame) return;
    if (lobby?.status !== 'playing') return;
    if (followedRef.current) return;
    followedRef.current = true;
    onOpenGame();
  }, [lobby?.status, onOpenGame, suppressAutoOpenGame]);


  if (error) {
    return (
      <main className="mx-auto w-full max-w-lg px-4 py-12">
        <Banner tone="danger">{error}</Banner>
        <div className="mt-4">
          <Button onClick={onLeave}>Back</Button>
        </div>
      </main>
    );
  }

  if (!lobby || !session) return <Spinner label="Opening the lobby" />;

  const me = lobby.members.find((m) => m.userId === session.user.id);
  const isHost = lobby.hostUserId === session.user.id;
  const spectatorsPresent = lobby.members.some((m) => m.role === 'spectator');

  // Arrow consts rather than function declarations: declarations hoist above
  // the null guard, so TypeScript cannot see that `lobby` is non-null inside
  // them even though it always is by the time they run.
  const run = async (work: () => Promise<unknown>) => {
    setActionError(null);
    try {
      await work();
      await refresh();
    } catch (caught) {
      setActionError(caught instanceof ApiRequestError ? caught.message : 'That did not work.');
    }
  };

  /**
   * Lowering a count past what somebody already submitted drops their newest
   * picks, so the host is asked first. Everything else goes straight through:
   * nudging a number is not a decision worth a dialog.
   */
  const setSlots = async (member: LobbyView['members'][number], slots: number | null) => {
    if (slots !== null && slots < member.submittedCount) {
      const dropping = member.submittedCount - slots;
      const confirmed = window.confirm(
        `${member.displayName} has ${member.submittedCount} in already.\n\n` +
          `Dropping them to ${slots} removes their ${dropping} newest ` +
          `${dropping === 1 ? 'pick' : 'picks'}.`,
      );
      if (!confirmed) return;
    }
    await run(() => api.setMemberSlots(lobby.id, member.userId, slots));
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(lobby.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Needs a secure context, and the code is on screen anyway.
    }
  };

  const endLobby = async () => {
    const confirmed = window.confirm(
      `End “${lobby.name}”?\n\nThis permanently removes the lobby, its entries, and any game in progress for everyone.`,
    );
    if (!confirmed) return;
    setActionError(null);
    try {
      await api.deleteLobby(lobby.id);
      onLeave();
    } catch (caught) {
      setActionError(
        caught instanceof ApiRequestError ? caught.message : 'Could not end the lobby.',
      );
    }
  };

  return (
    <main className="mx-auto w-full max-w-2xl px-4 py-8">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="truncate text-xl font-semibold tracking-tight">{lobby.name}</h1>
          <p className="mt-1 flex items-center gap-2 text-sm text-text-muted">
            <button
              type="button"
              onClick={() => void copyCode()}
              className="rounded bg-surface-raised px-2 py-1 font-mono tracking-widest hover:text-text"
              title="Copy the code"
            >
              {lobby.code}
            </button>
            {copied && <span className="text-xs text-success">copied</span>}
            {!connected && <span className="text-xs text-warning">reconnecting…</span>}
          </p>
        </div>
        <div className="flex gap-2">
          {isHost && (
            <Button variant="danger" onClick={() => void endLobby()}>
              End lobby
            </Button>
          )}
          <Button
            variant="ghost"
            onClick={() => void run(async () => {
              await api.leaveLobby(lobby.id);
              onLeave();
            })}
          >
            Leave
          </Button>
        </div>
      </header>

      {actionError && (
        <div className="mb-4">
          <Banner tone="danger">{actionError}</Banner>
        </div>
      )}

      {(lobby.status === 'playing' || lobby.status === 'finished') && (
        <div className="mb-4">
          <Card className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm">
              {lobby.status === 'finished' ? 'This game is over.' : 'The game is running.'}
            </p>
            <div className="flex flex-wrap gap-2">
              {/* Still here for the case where somebody walked back to the
                  lobby on purpose, or the automatic jump above did not fire. */}
              <Button onClick={onOpenGame}>
                {lobby.status === 'finished' ? 'See the results' : 'Back to the game'}
              </Button>
              {lobby.status === 'finished' && isHost && (
                // Without this a finished lobby is a dead end: the entries are
                // still sitting there from last game, so the only way to play
                // again is to make a new lobby and have everyone rejoin.
                <Button
                  variant="secondary"
                  onClick={() => void run(() => api.resetLobby(lobby.id))}
                >
                  Play again
                </Button>
              )}
            </div>
          </Card>
        </div>
      )}

      {isHost && lobby.status !== 'playing' && lobby.status !== 'finished' && (
        <div className="mb-4">
          <Card className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium">
                {!lobby.slotPlan.ok
                  ? // Every allotment is zero while the plan is broken, which
                    // would otherwise read as "Everything is in."
                    'The song counts do not add up yet.'
                  : lobby.totalSubmitted >= lobby.totalRequired
                    ? 'Everything is in.'
                    : `Waiting on ${lobby.totalRequired - lobby.totalSubmitted} more`}
              </p>
            </div>
            <Button
              onClick={() =>
                void run(async () => {
                  await api.startGame(lobby.id);
                  onOpenGame();
                })
              }
              disabled={!lobby.slotPlan.ok || lobby.totalSubmitted < lobby.totalRequired}
            >
              Start the game
            </Button>
          </Card>
        </div>
      )}

      {lobby.settings.themePrompt && (
        <div className="mb-4">
          <Banner>
            <strong>Theme:</strong> {lobby.settings.themePrompt}
          </Banner>
        </div>
      )}

      {!lobby.slotPlan.ok && (
        <div className="mb-4">
          <Banner tone="danger">{lobby.slotPlan.message}</Banner>
        </div>
      )}

      <Card className="mb-4">
        <div className="mb-2 flex items-baseline justify-between gap-3">
          <h2 className="font-medium">
            Everyone <span className="text-text-faint">({lobby.members.length})</span>
          </h2>
          <span className="text-sm text-text-muted">
            {lobby.totalSubmitted} / {lobby.totalRequired} entries in
          </span>
        </div>

        {lobby.slotPlan.ok && (
          <p className="mb-2 text-xs text-text-faint">{lobby.slotPlan.summary}</p>
        )}

        {/* A plain progress bar beats a spinner here: the question everyone is
            actually asking is "are we waiting on anyone". */}
        <div
          role="progressbar"
          aria-valuenow={lobby.totalSubmitted}
          aria-valuemin={0}
          aria-valuemax={lobby.totalRequired}
          aria-label="Entries submitted"
          className="mb-3 h-1.5 overflow-hidden rounded-full bg-surface-raised"
        >
          <div
            className="h-full rounded-full bg-accent transition-[width] duration-300"
            style={{
              width: `${lobby.totalRequired > 0 ? Math.min(100, (lobby.totalSubmitted / lobby.totalRequired) * 100) : 0}%`,
            }}
          />
        </div>

        <ul className="divide-y divide-border">
          {lobby.members.map((member) => (
            <MemberRow
              key={member.userId}
              member={member}
              isHost={member.userId === lobby.hostUserId}
              canManage={isHost && member.userId !== lobby.hostUserId}
              // The host submits songs too, so their own row gets a stepper
              // even though they cannot make themselves a watcher.
              canSetSlots={isHost && member.role !== 'spectator'}
              maxSlots={lobby.bracketSize}
              onToggleRole={() =>
                void run(() =>
                  api.setMemberRole(
                    lobby.id,
                    member.userId,
                    member.role === 'spectator' ? 'player' : 'spectator',
                  ),
                )
              }
              onSetSlots={(slots) => void setSlots(member, slots)}
            />
          ))}
        </ul>

        {spectatorsPresent && !lobby.settings.spectatorsVote && (
          <p className="mt-3 text-xs text-text-faint">
            Watchers don't vote.
          </p>
        )}
      </Card>

      {me && me.role !== 'spectator' && me.slotsAllotted > 0 && (
        <div className="mb-4">
          <EntrySubmit
            lobbyId={lobby.id}
            slots={me.slotsAllotted}
            spotifyAvailable
            onChanged={() => void refresh()}
          />
        </div>
      )}

      {me?.role === 'spectator' && (
        <div className="mb-4">
          <Banner>You're watching. Ask the host to make you a player to submit.</Banner>
        </div>
      )}

      <LobbySettingsPanel lobby={lobby} isHost={isHost} onSettingsApplied={() => void refresh()} />

      <p className="mt-4 text-center text-xs text-text-faint">
        Bracket seed {lobby.rngSeed} · the draw is reproducible, so nobody can claim it was rigged
      </p>
    </main>
  );
}
